/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import { createContext, useCallback, useMemo, useState } from 'react';
import {
    useWorkOrderPhotoTypesQuery,
    useWorkOrderVideoTypesQuery,
} from 'common/stores/redux/MetaStore/MetaStoreApi';
import DocumentRequirementsPhotosUploaded, {
    isVideo,
} from 'common/components/document/DocumentRequirementsPhotos/DocumentRequirementsPhotosUploaded';
import { Flex } from '@motili/ui';
import _ from 'lodash';
import { AcceptedFile } from 'common/components/file-upload/FileUploadTypes';
import {
    Requirement,
    Photo,
    PhotoApproval,
    AssetType,
    Document,
    cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser,
} from '../DocumentRequirementsPhotos/DocumentRequirementsPhotos';
import { DocumentRequirementsPhotosUploadDrawer } from '../DocumentRequirementsPhotosDrawer';
import { hasVideoRequirement } from '../../../utils/requirements/requirement-utils';

interface AdminDocumentRequirementsPhotosProps {
    clientRequirements?: Requirement[];
    projectRequirements?: Requirement[];
    photos?: Photo[];
    disabled?: boolean;
    acls?: boolean;
    removeAcls?: boolean;
    handleUploadPhotosOnClick?: (
        file: File,
        typeId: string,
        description: string,
        requiresPullAndClean: boolean,
        requirementTypeId: string
    ) => Promise<void>;
    handleReUploadPhotoOnClick?: (photoId: string) => void;
    handleSavePhotoOnClick?: (photo: Photo) => void;
    handleRemovePhotoOnClick?: (photoId: string) => void;
    handleEditPhotoRequirements?: (requirement: Requirement) => void;
    handleEditUploadedPhotoRequirement?: (
        photo: Photo,
        requirement: Requirement
    ) => void;
    handleGetApprovalHistoryPhotoRequirement?: (photoId: string) => void;
    handleSaveApprovalPhotoRequirement?: (
        photoId: string,
        status: string,
        notes?: string
    ) => void;
    handleBulkApprovalPhotoRequirement?: (
        photoIds: string[],
        status: string,
        notes?: string
    ) => void;
    handleEditApprovalPhotoRequirement?: (
        photoId: string,
        approval: PhotoApproval
    ) => void;
    document?: Document;
    assetTypes?: AssetType[];
    isShowStatusFilter: boolean;
    setAcceptedFiles: (acceptFile: AcceptedFile[]) => void;
    acceptedFiles: AcceptedFile[];
}

const AdminDocumentRequirementsPhotos = ({
    handleUploadPhotosOnClick,
    handleReUploadPhotoOnClick,
    handleSavePhotoOnClick,
    handleRemovePhotoOnClick,
    handleEditPhotoRequirements,
    handleEditUploadedPhotoRequirement,
    handleGetApprovalHistoryPhotoRequirement,
    handleSaveApprovalPhotoRequirement,
    handleBulkApprovalPhotoRequirement,
    handleEditApprovalPhotoRequirement,
    document,
    clientRequirements = [],
    projectRequirements = [],
    photos = [],
    disabled = false,
    acls = false,
    removeAcls = false,
    assetTypes = [],
    isShowStatusFilter = true,
    setAcceptedFiles,
    acceptedFiles,
}: AdminDocumentRequirementsPhotosProps) => {
    const [currentPhotoTypeId, setCurrentPhotoTypeId] = useState<string | null>(
        null
    );
    const [filter, setFilter] = useState<string>('ALL');

    const { data: workOrderPhotoTypes } = useWorkOrderPhotoTypesQuery();
    const { data: workOrderVideoTypes } = useWorkOrderVideoTypesQuery();

    const photoTypeRequirements = useMemo(() => {
        const _clientRequirements = clientRequirements || [];
        const _projectRequirements = projectRequirements || [];
        return [..._clientRequirements, ..._projectRequirements]?.filter(
            requirement => requirement.requirementTypeId === 'PHOTO'
        );
    }, [clientRequirements, projectRequirements]);
    const videoTypeRequirements = useMemo(() => {
        const _clientRequirements = clientRequirements || [];
        const _projectRequirements = projectRequirements || [];
        return [..._clientRequirements, ..._projectRequirements]?.filter(
            requirement => requirement.requirementTypeId === 'VIDEO'
        );
    }, [clientRequirements, projectRequirements]);

    const photoTypeContextValue = useMemo(
        () => ({ currentPhotoTypeId, setCurrentPhotoTypeId }),
        [currentPhotoTypeId, setCurrentPhotoTypeId]
    );

    const handleCloseOnClick = useCallback(() => {
        cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
            acceptedFiles
        );
        setAcceptedFiles([]);
        setCurrentPhotoTypeId(null);
    }, [acceptedFiles, setAcceptedFiles]);

    const handleUploadOnClick = async ({
        typeId,
        description,
        requiresPullAndClean,
        requirementTypeId,
    }: {
        typeId: string;
        description: string;
        requiresPullAndClean: boolean;
        requirementTypeId: string;
    }) => {
        const file = acceptedFiles[0];
        try {
            await handleUploadPhotosOnClick?.(
                file,
                typeId,
                description,
                requiresPullAndClean,
                requirementTypeId
            );
            handleCloseOnClick();
        } catch (e) {
            handleCloseOnClick();
        }
    };

    return (
        <DocumentRequirementsPhotosUploadModalContext.Provider
            value={photoTypeContextValue}
        >
            <DocumentRequirementsPhotosUploadDrawer
                handleCloseOnClick={handleCloseOnClick}
                handleCancelOnClick={handleCloseOnClick}
                handleUploadOnClick={({
                    typeId,
                    description,
                    requiresPullAndClean,
                    requirementTypeId,
                }: {
                    typeId?: string;
                    description: string;
                    requiresPullAndClean: boolean;
                    requirementTypeId: string;
                }) =>
                    handleUploadOnClick({
                        typeId: typeId || currentPhotoTypeId!,
                        description,
                        requiresPullAndClean,
                        requirementTypeId,
                    })
                }
                photoTypes={workOrderPhotoTypes || []}
                videoTypes={workOrderVideoTypes || []}
                currentPhotoTypeId={currentPhotoTypeId}
                photoTypeRequirements={photoTypeRequirements}
                videoTypeRequirements={videoTypeRequirements}
                photos={photos}
                fileName={acceptedFiles[0]?.name}
                imgSrc={acceptedFiles[0]?.preview}
                mimeType={acceptedFiles[0]?.type}
                open={acceptedFiles.length > 0}
                file={acceptedFiles[0]}
                enabledVideoRecording={document?.client?.enabledVideoRecording}
                hasVideoRequirement={
                    hasVideoRequirement(document?.clientRequirements) ||
                    hasVideoRequirement(document?.projectRequirements)
                }
            />
            <Flex direction='column' fullWidth>
                <DocumentRequirementsPhotosUploaded
                    photos={photos}
                    filter={filter}
                    setFilter={setFilter}
                    photoTypes={workOrderPhotoTypes || []}
                    videoTypes={workOrderVideoTypes || []}
                    photoTypeRequirements={photoTypeRequirements}
                    videoTypeRequirements={videoTypeRequirements}
                    disabled={disabled}
                    acls={acls}
                    removeAcls={removeAcls}
                    handleSavePhotoOnClick={handleSavePhotoOnClick}
                    handleUploadPhotoOnClick={handleUploadPhotosOnClick}
                    handleReUploadPhotoOnClick={handleReUploadPhotoOnClick}
                    handleRemovePhotoOnClick={handleRemovePhotoOnClick}
                    handleEditUploadedPhotoRequirement={
                        handleEditUploadedPhotoRequirement
                    }
                    handleEditPhotoRequirements={handleEditPhotoRequirements}
                    handleGetApprovalHistoryPhotoRequirement={
                        handleGetApprovalHistoryPhotoRequirement
                    }
                    handleSaveApprovalPhotoRequirement={
                        handleSaveApprovalPhotoRequirement
                    }
                    handleBulkApprovalPhotoRequirement={
                        handleBulkApprovalPhotoRequirement
                    }
                    handleEditApprovalPhotoRequirement={
                        handleEditApprovalPhotoRequirement
                    }
                    document={document}
                    assetTypes={assetTypes}
                    isShowStatusFilter={isShowStatusFilter}
                />
            </Flex>
        </DocumentRequirementsPhotosUploadModalContext.Provider>
    );
};

interface DocumentRequirementsPhotosUploadModalContextType {
    currentPhotoTypeId: string | null;
    setCurrentPhotoTypeId: (value: string | null) => void;
}

export const DocumentRequirementsPhotosUploadModalContext =
    createContext<DocumentRequirementsPhotosUploadModalContextType>({
        currentPhotoTypeId: null,
        setCurrentPhotoTypeId: () => {},
    });

export const isPhotoRequirementMet = (photoType, mediaList) =>
    _.some(
        mediaList,
        media => !isVideo(media) && media.requirement?.photoTypeId === photoType
    );
export const isRequirementMet = (type, mediaList) =>
    _.some(
        mediaList,
        media => (media?.requirementTypeId === 'VIDEO' ?
            media.requirement?.photoTypeId === type :
            !isVideo(media) && media.requirement?.photoTypeId === type)
    );
export default AdminDocumentRequirementsPhotos;
